/**
 * STATS & TRACKING — статистика использования услуг/категорий, реорганизация по популярности.
 *
 * Phase 5 modularization (Sprint 6, 2026-05-07).
 * Извлечено из public/js/app.js (DOMAIN: ESCAPE HTML & MISC RENDER HELPERS — назван неудачно).
 *
 * Содержит:
 *   - escapeHtml(text)             — wrapper над StringUtils.escapeHtml (для inline-вызовов)
 *   - validateAndFixTemplateCategories() — миграция услуг с битой категорией
 *   - Глобальная статистика (НИКОГДА не сбрасывается, копится для аналитики):
 *       loadGlobalStats / initGlobalStats / saveGlobalStats / trackGlobalUsage
 *   - Локальная статистика (для сортировки в UI, периодически сбрасывается):
 *       trackCategoryUsage / trackTemplateUsage
 *   - Реорганизация по популярности:
 *       checkCategoryReorganization / checkTemplateReorganization
 *       reorganizeCategoriesByPopularity / reorganizeTemplatesByPopularity
 *   - Helpers:
 *       getCategoryPopularity / getCategoryUsageLevel
 *
 * @module app/stats-tracking
 */

/**
 * Прикрепляет stats/tracking методы к prototype класса.
 * @param {Function} ClassRef - класс ProfessionalQuoteCalculator
 */
export function attachStatsTracking(ClassRef) {
    Object.assign(ClassRef.prototype, {
        escapeHtml(text) {
            // Phase 2: делегирование к StringUtils
            return window.StringUtils.escapeHtml(text);
        },

        // Migration v3: copyRegionCatalog() and copySelectedServicesToRegion() removed
        // These methods used localStorage - catalog operations now use server API

        // ======= КОНЕЦ УПРАВЛЕНИЯ РЕГИОНАМИ =======

        // Загрузка глобальной статистики (НИКОГДА не сбрасывается)
        loadGlobalStats() {
            const saved = localStorage.getItem('quoteCalc_globalStats');
            if (saved) {
                try {
                    this.globalStats = JSON.parse(saved);
                    console.log('Global statistics loaded:', this.globalStats);
                } catch (error) {
                    console.error('Error loading global stats:', error);
                    // Initialize empty stats on error
                    this.initGlobalStats();
                }
            } else {
                this.initGlobalStats();
            }
        },

        // Инициализация глобальной статистики
        initGlobalStats() {
            this.globalStats = {
                services: {},
                categories: {},
                totalServicesAdded: 0,
                firstUsage: null,
                lastUsage: null
            };
            this.saveGlobalStats();
        },

        // Сохранение глобальной статистики
        saveGlobalStats() {
            try {
                localStorage.setItem('quoteCalc_globalStats', JSON.stringify(this.globalStats));
                console.log('Global statistics saved');
            } catch (error) {
                console.error('Error saving global stats:', error);
            }
        },

        // Проверяет и исправляет категории услуг (только при загрузке данных)
        validateAndFixTemplateCategories() {
            // Получаем список ID всех существующих категорий (исключая системные)
            const validCategoryIds = this.categories.map(cat => cat.id);
            let fixedCount = 0;

            // Проверяем каждую услугу
            this.templates.forEach(template => {
                // Если у услуги нет категории, пустая категория, или категория не существует
                if (!template.category || template.category === '' || !validCategoryIds.includes(template.category)) {
                    const oldCategory = template.category || 'none';
                    template.category = 'undefined';
                    fixedCount++;
                    console.log(`Услуга "${template.name}" перемещена из категории "${oldCategory}" в "Не определено"`);
                }
            });

            // Если были исправления, сохраняем
            if (fixedCount > 0) {
                this.saveToLocalStorage();
                console.log(`Исправлено категорий: ${fixedCount}`);
            }
        },

        trackCategoryUsage(categoryId) {
            if (!this.categoryStats.usage[categoryId]) {
                this.categoryStats.usage[categoryId] = 0;
            }
            this.categoryStats.usage[categoryId]++;
            this.saveCategoryStats();
            console.log(`Tracked usage for ${categoryId}: ${this.categoryStats.usage[categoryId]}`);
        },

        trackTemplateUsage(templateId) {
            if (!this.templateStats.usage[templateId]) {
                this.templateStats.usage[templateId] = 0;
            }
            this.templateStats.usage[templateId]++;
            this.saveTemplateStats();
            console.log(`Tracked template usage for ${templateId}: ${this.templateStats.usage[templateId]}`);
        },

        // Отслеживание глобальной статистики (НИКОГДА не сбрасывается)
        trackGlobalUsage(templateId, serviceName, categoryId) {
            const now = Date.now();

            // Обновляем общий счетчик
            this.globalStats.totalServicesAdded++;

            // Устанавливаем первое использование если еще не установлено
            if (!this.globalStats.firstUsage) {
                this.globalStats.firstUsage = now;
            }

            // Обновляем последнее использование
            this.globalStats.lastUsage = now;

            // Статистика по услугам
            if (templateId) {
                if (!this.globalStats.services[templateId]) {
                    this.globalStats.services[templateId] = {
                        count: 0,
                        name: serviceName,
                        category: categoryId || 'undefined',
                        firstUsed: now,
                        lastUsed: now
                    };
                }
                this.globalStats.services[templateId].count++;
                this.globalStats.services[templateId].lastUsed = now;
            }

            // Статистика по категориям
            if (categoryId) {
                const category = this.categories.find(c => c.id === categoryId);
                const categoryName = category ? category.name : 'Неизвестно';

                if (!this.globalStats.categories[categoryId]) {
                    this.globalStats.categories[categoryId] = {
                        count: 0,
                        name: categoryName,
                        firstUsed: now,
                        lastUsed: now
                    };
                }
                this.globalStats.categories[categoryId].count++;
                this.globalStats.categories[categoryId].lastUsed = now;
            }

            // Сохраняем глобальную статистику
            this.saveGlobalStats();

            console.log(`Global stats tracked: service=${serviceName}, category=${categoryId}, total=${this.globalStats.totalServicesAdded}`);
        },

        checkCategoryReorganization() {
            const now = Date.now();
            const lastReorg = this.categoryStats.lastReorganization;

            // If never reorganized or 3 days passed
            if (!lastReorg || (now - lastReorg) >= this.categoryStats.reorganizationInterval) {
                this.reorganizeCategoriesByPopularity();
            }
        },

        checkTemplateReorganization() {
            const now = Date.now();
            const lastReorg = this.templateStats.lastReorganization;

            // If never reorganized or 3 days passed
            if (!lastReorg || (now - lastReorg) >= this.templateStats.reorganizationInterval) {
                this.reorganizeTemplatesByPopularity();
            }
        },

        reorganizeCategoriesByPopularity() {
            // Get total usage for percentage calculation
            const totalUsage = Object.values(this.categoryStats.usage).reduce((sum, count) => sum + count, 0);

            if (totalUsage === 0) {
                // No usage data yet, skip reorganization
                console.log('Reorganization skipped: no usage data');
                return;
            }

            console.log('Before reorganization:', this.categories.map(c => `${c.name}:${this.categoryStats.usage[c.id] || 0}`));

            // Sort categories by usage count (descending)
            this.categories.sort((a, b) => {
                const usageA = this.categoryStats.usage[a.id] || 0;
                const usageB = this.categoryStats.usage[b.id] || 0;
                return usageB - usageA;
            });

            console.log('After reorganization:', this.categories.map(c => `${c.name}:${this.categoryStats.usage[c.id] || 0}`));

            // Save new order and update timestamp
            this.categoryStats.lastReorganization = Date.now();
            this.saveCategories();
            this.saveCategoryStats();
            this.renderCategories();
            this.renderCategoryOptions();

            console.log('Categories reorganized by popularity');
        },

        reorganizeTemplatesByPopularity() {
            // Get total usage for percentage calculation
            const totalUsage = Object.values(this.templateStats.usage).reduce((sum, count) => sum + count, 0);

            if (totalUsage === 0) {
                // No usage data yet, skip reorganization
                console.log('Template reorganization skipped: no usage data');
                return;
            }

            console.log('Before template reorganization:', this.templates.slice(0, 5).map(t => `${t.name}:${this.templateStats.usage[t.id] || 0}`));

            // Sort templates by usage count (descending), within each category
            this.templates.sort((a, b) => {
                // First, sort by category (keep categories together)
                if (a.category !== b.category) {
                    // Find category order
                    const catIndexA = this.categories.findIndex(c => c.id === a.category);
                    const catIndexB = this.categories.findIndex(c => c.id === b.category);
                    return catIndexA - catIndexB;
                }
                // Within same category, sort by usage (descending)
                const usageA = this.templateStats.usage[a.id] || 0;
                const usageB = this.templateStats.usage[b.id] || 0;
                return usageB - usageA;
            });

            console.log('After template reorganization:', this.templates.slice(0, 5).map(t => `${t.name}:${this.templateStats.usage[t.id] || 0}`));

            // Save new order and update timestamp
            this.templateStats.lastReorganization = Date.now();
            this.saveToLocalStorage();
            this.saveTemplateStats();
            this.renderTemplates();

            console.log('Templates reorganized by popularity within categories');
        },

        getCategoryPopularity(categoryId) {
            const usage = this.categoryStats.usage[categoryId] || 0;
            const totalUsage = Object.values(this.categoryStats.usage).reduce((sum, count) => sum + count, 0);

            if (totalUsage === 0) return 0;
            return Math.round((usage / totalUsage) * 100);
        },

        getCategoryUsageLevel(categoryId) {
            const popularity = this.getCategoryPopularity(categoryId);
            if (popularity >= 30) return 'high';
            if (popularity >= 15) return 'medium';
            if (popularity > 0) return 'low';
            return 'none';
        },
    });
}
